import { createFileRoute } from "@tanstack/react-router";
import { SimpleLegalLayout, SimpleLegalSection } from "@/components/legal/SimpleLegalLayout";

export const Route = createFileRoute("/terms")({
  head: () => ({
    title: "terms of service - clove.moe",
    meta: [
      {
        name: "description",
        content:
          "terms of service governing your use of clove.moe, the bio page creation platform operated by clove.moe.",
      },
      { property: "og:title", content: "terms of service - clove.moe" },
      {
        property: "og:description",
        content: "terms and conditions governing the use of clove.moe",
      },
    ],
  }),
  component: TermsOfServicePage,
});

export function TermsOfServicePage() {
  return (
    <SimpleLegalLayout
      title="Terms of Service"
      subtitle="Last updated September 15, 2025"
    >
      <SimpleLegalSection title="1. Acceptance of Terms">
        <p>
          These Terms of Service ("Terms") constitute a legally binding
          agreement between you ("User," "you," or "your") and Clove.moe
          ("Clove.moe," "we," "us," or "our") governing your access to and use
          of the Clove.moe website, application programming interfaces, and
          related services (collectively, the "Service"). By accessing or using
          the Service, you acknowledge that you have read, understood, and
          agree to be bound by these Terms. If you do not agree to these Terms,
          you must not access or use the Service.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="2. Description of the Service">
        <p>
          Clove.moe is a platform that enables users to create, customize, and
          publish personal profile pages. The Service includes, without
          limitation, the following functionality:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Composition and styling of profile pages using the Clove.moe
            markup language ("BML") or sanitized hypertext markup language and
            cascading style sheets ("HTML/CSS");
          </li>
          <li>Upload and hosting of media assets within prescribed limits;</li>
          <li>
            Publication of profile pages at a unique username of the User's
            choosing, subject to availability and these Terms; and
          </li>
          <li>Access to basic, non-identifying engagement metrics.</li>
        </ul>
        <p>
          We reserve the right to modify, suspend, or discontinue any aspect of
          the Service at any time, with or without notice, and without
          liability to you.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="3. User Accounts">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            You agree to provide accurate, current, and complete information
            during registration and to update such information as necessary to
            maintain its accuracy;
          </li>
          <li>
            You are solely responsible for safeguarding the confidentiality of
            your account credentials and for all activity that occurs under
            your account;
          </li>
          <li>
            You are responsible for any and all acts or omissions of any person
            who accesses the Service using your account credentials;
          </li>
          <li>
            You may delete your account at any time through the Service, which
            constitutes termination of your agreement with us under these Terms
            with respect to future use.
          </li>
        </ul>
      </SimpleLegalSection>

      <SimpleLegalSection title="4. Acceptable Conduct">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            You shall not engage in harassment, abuse, or the distribution of
            unlawful content;
          </li>
          <li>
            You shall not access the Service through automated means, including
            scraping or crawling, without our prior written authorization;
          </li>
          <li>
            You shall not attempt to circumvent, disable, or otherwise
            interfere with security-related features of the Service;
          </li>
          <li>
            You shall respect the intellectual property rights of Clove.moe and
            of third parties.
          </li>
        </ul>
      </SimpleLegalSection>

      <SimpleLegalSection title="5. Content Ownership and License">
        <p>
          You retain all right, title, and interest in and to the content you
          submit to the Service ("User Content"), including profile content and
          uploaded media. By submitting User Content, you grant Clove.moe a
          worldwide, non-exclusive, royalty-free, sublicensable license to
          store, process, cache, and display such User Content solely for the
          purpose of operating, maintaining, and improving the Service.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="6. Acceptable Use">
        <p>
          The Service is intended to facilitate the creation of personal
          profile pages, professional portfolios, and other forms of creative
          self-expression. You agree to use the Service solely for lawful
          purposes and in accordance with these Terms.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="7. Prohibited Content">
        <ul className="list-disc pl-5 space-y-1">
          <li>Content that promotes hatred, violence, extremism, or exploitation;</li>
          <li>Malware, phishing schemes, or attempts to bypass security controls;</li>
          <li>Sexually explicit or pornographic material;</li>
          <li>Unauthorized or infringing copyrighted works; or</li>
          <li>Content that impersonates or misrepresents any person or entity.</li>
        </ul>
      </SimpleLegalSection>

      <SimpleLegalSection title="8. Intellectual Property">
        <p>
          All trademarks, service marks, brand elements, and the software,
          source code, and content comprising the Service are and shall remain
          the exclusive property of Clove.moe or its licensors. Nothing in these
          Terms grants you any right to reproduce, replicate, or misrepresent
          such intellectual property.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="9. Service Availability">
        <p>
          We endeavor to maintain a high degree of availability but make no
          warranty or guarantee of uninterrupted or error-free operation. The
          Service may be unavailable due to scheduled or unscheduled
          maintenance, outages, or other circumstances beyond our reasonable
          control, and such interruptions may occur without prior notice.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="10. Disclaimers">
        <p>
          The Service is provided on an "as is" and "as available" basis. To the
          maximum extent permitted by applicable law, Clove.moe disclaims all
          warranties, whether express, implied, statutory, or otherwise,
          including any implied warranties of merchantability, fitness for a
          particular purpose, and non-infringement.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="11. Limitation of Liability">
        <p>
          To the maximum extent permitted by applicable law, Clove.moe's total
          aggregate liability arising out of or relating to these Terms or your
          use of the Service shall not exceed one hundred United States Dollars
          (US $100). In no event shall Clove.moe be liable for any indirect,
          incidental, special, consequential, or punitive damages.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="12. Termination">
        <p>
          We may suspend or terminate your account and access to the Service,
          with or without notice, if you violate these Terms or engage in
          conduct that harms the Service or other users. Upon termination, the
          provisions of these Terms that by their nature should survive shall
          survive, including those relating to ownership, disclaimers, and
          limitations of liability.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="13. Governing Law">
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of the State of Delaware, United States, without regard to its
          conflict of laws principles.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="14. Changes to These Terms">
        <p>
          We may revise these Terms from time to time. Material changes will be
          reflected by an updated "Last Updated" date. Your continued use of the
          Service following the effective date of any such changes constitutes
          your acceptance of the revised Terms.
        </p>
      </SimpleLegalSection>

      <SimpleLegalSection title="15. Contact">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Email:{" "}
            <a
              href="mailto:support@clove.moe"
              className="text-primary hover:underline"
            >
              support@clove.moe
            </a>
          </li>
          <li>
            Discord:{" "}
            <a
              href="https://discord.gg/emrucgSEq2"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              join the server
            </a>
          </li>
        </ul>
      </SimpleLegalSection>
    </SimpleLegalLayout>
  );
}
